import { Link } from 'react-router-dom'
import HeroCTA from '../components/HeroCTA'
import './TrainingAndConsultationPage.css'

function TrainingAndConsultationPage() {
  return (
    <div className="training-consultation-page">
      <section className="page-hero">
        <div className="container">
          <h1>Training & Consultation</h1>
          <p className="page-hero-subtitle">
            Comprehensive training programs and expert consultation to ensure your team is equipped with the knowledge and skills they need
          </p>
        </div>
      </section>

      <section className="training-content">
        <div className="container">
          <div className="training-intro">
            <h2>Why Training & Consultation Matters</h2>
            <p>
              Proper training and consultation are essential for maximizing the value of your laboratory equipment investment. 
              Our comprehensive programs ensure your team can safely, efficiently, and effectively operate your equipment while 
              maintaining compliance with industry standards.
            </p>
            <div className="training-intro-questions">
              <h3>Key Questions for Laboratory Operations & Maintenance Teams</h3>
              <ul>
                <li>Is your O&M crew truly in service to the mission of your laboratory?</li>
                <li>Is there full cooperation between your O&M crew and the laboratory staff?</li>
                <li>How can your facility operations reduce your lab's energy costs?</li>
              </ul>
              <p>
                Laboratory Specialists offers specialized training and consulting services for Laboratory Operations and 
                Maintenance personnel. Our training is specific to laboratory-related equipment and subjects, with a strong 
                emphasis on safety for both O&M staff and laboratory personnel.
              </p>
            </div>
          </div>

          <div className="training-sections">
            <div className="training-section">
              <div className="training-section-icon">🎓</div>
              <h2>Training Services</h2>
              <p>
                Our training programs are designed to empower your team with the knowledge and skills needed to operate 
                laboratory equipment safely and effectively. We offer flexible training options to fit your schedule and needs.
              </p>
              
              <div className="training-details">
                <div className="training-detail-item">
                  <h3>Equipment Operation Training</h3>
                  <p>
                    Hands-on training covering all aspects of equipment operation, from basic functions to advanced features. 
                    Your team will learn proper usage techniques, troubleshooting, and best practices.
                  </p>
                  <ul>
                    <li>Basic operation and controls</li>
                    <li>Advanced features and capabilities</li>
                    <li>Troubleshooting common issues</li>
                    <li>Optimizing equipment performance</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Safety and Compliance Training</h3>
                  <p>
                    Essential safety training to ensure your team understands proper safety protocols, regulatory requirements, 
                    and compliance standards for laboratory equipment operation.
                  </p>
                  <ul>
                    <li>OSHA safety standards</li>
                    <li>Laboratory safety protocols</li>
                    <li>Regulatory compliance requirements</li>
                    <li>Emergency procedures</li>
                    <li>Personal protective equipment (PPE) usage</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Maintenance Procedures Training</h3>
                  <p>
                    Training on preventive maintenance, routine care, and basic troubleshooting to help your team maintain 
                    equipment in optimal condition and reduce downtime.
                  </p>
                  <ul>
                    <li>Routine maintenance schedules</li>
                    <li>Cleaning and care procedures</li>
                    <li>Basic troubleshooting techniques</li>
                    <li>When to call for professional service</li>
                    <li>Maintenance documentation</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Laboratory Operations & Maintenance (O&M) Training</h3>
                  <p>
                    Specialized training programs designed specifically for Operations and Maintenance personnel working in 
                    laboratory environments. Our O&M training emphasizes safety, efficiency, and effective collaboration with 
                    laboratory staff.
                  </p>
                  <ul>
                    <li>Communications, Relationships, and Teamwork</li>
                    <li>Working in a working lab environment</li>
                    <li>Lab O&M contribution to Environmental Health & Safety (EHS)</li>
                    <li>Lab O&M contribution to lab quality assurance</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Laboratory Systems & Equipment Training</h3>
                  <p>
                    Comprehensive training on laboratory building systems and specialized equipment to ensure proper operation, 
                    maintenance, and safety compliance.
                  </p>
                  <ul>
                    <li>Laboratory Building Management Systems</li>
                    <li>Vacuum systems, bottled gases, and lab refrigerators</li>
                    <li>Chemical Fume Hoods and Biological Safety Cabinets</li>
                    <li>Flammables, Acids and Corrosives Storage</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Energy Efficiency & Conservation Training</h3>
                  <p>
                    Training on energy conservation strategies and technologies that reduce laboratory energy costs without 
                    compromising safety or operational effectiveness.
                  </p>
                  <ul>
                    <li>Conserving energy without compromising safety</li>
                    <li>Introduction to latest laboratory facility energy conserving devices</li>
                    <li>Knowledge to determine which energy solutions are appropriate for your facility</li>
                  </ul>
                </div>

                <div className="training-detail-item">
                  <h3>Customized Training Programs</h3>
                  <p>
                    We develop tailored training programs specific to your equipment, industry requirements, and team needs. 
                    Our customized approach ensures maximum relevance and effectiveness.
                  </p>
                  <ul>
                    <li>Equipment-specific training</li>
                    <li>Industry-specific protocols</li>
                    <li>Role-based training modules</li>
                    <li>Flexible scheduling options</li>
                    <li>Ongoing support and refresher courses</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="training-section">
              <div className="training-section-icon">💡</div>
              <h2>Consultation Services</h2>
              <p>
                Expert consultation to help you make informed decisions about your laboratory setup, equipment selection, 
                and operational strategies. Our consultants work with you to develop solutions tailored to your specific needs.
              </p>

              <div className="consultation-details">
                <div className="consultation-detail-item">
                  <h3>Lab Design and Layout Consultation</h3>
                  <p>
                    Strategic planning for your laboratory space to optimize workflow, safety, and efficiency. We help you 
                    design layouts that maximize productivity while ensuring compliance with safety standards.
                  </p>
                </div>

                <div className="consultation-detail-item">
                  <h3>Equipment Selection Guidance</h3>
                  <p>
                    Expert advice on selecting the right equipment for your specific applications, budget, and requirements. 
                    We help you make informed decisions that align with your goals.
                  </p>
                </div>

                <div className="consultation-detail-item">
                  <h3>Budget Planning and Optimization</h3>
                  <p>
                    Strategic financial planning to help you optimize your equipment investment. We provide cost analysis, 
                    ROI projections, and budget optimization strategies.
                  </p>
                </div>

                <div className="consultation-detail-item">
                  <h3>Compliance and Safety Assessment</h3>
                  <p>
                    Comprehensive assessments to ensure your laboratory meets all regulatory requirements and safety standards. 
                    We identify areas for improvement and provide actionable recommendations.
                  </p>
                </div>

                <div className="consultation-detail-item">
                  <h3>Project Management Support</h3>
                  <p>
                    End-to-end project management support for laboratory setup, equipment installation, and operational 
                    transitions. We help ensure smooth project execution from planning to completion.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="training-options">
            <h2>Training Delivery Options</h2>
            <div className="options-grid">
              <div className="option-card">
                <h3>On-Site Training</h3>
                <p>
                  Our trainers come to your facility to provide hands-on training with your actual equipment. This option 
                  offers the most personalized and relevant training experience.
                </p>
                <ul>
                  <li>Training with your actual equipment</li>
                  <li>Personalized instruction</li>
                  <li>Immediate application of skills</li>
                  <li>Team-based learning</li>
                </ul>
              </div>

              <div className="option-card">
                <h3>Remote Training</h3>
                <p>
                  Virtual training sessions conducted via video conferencing. Ideal for teams that are distributed or when 
                  on-site visits aren't feasible.
                </p>
                <ul>
                  <li>Flexible scheduling</li>
                  <li>Cost-effective option</li>
                  <li>Recorded sessions for review</li>
                  <li>Accessible from anywhere</li>
                </ul>
              </div>

              <div className="option-card">
                <h3>Hybrid Training</h3>
                <p>
                  A combination of remote and on-site training that provides the flexibility of virtual learning with the 
                  hands-on benefits of in-person instruction.
                </p>
                <ul>
                  <li>Best of both worlds</li>
                  <li>Flexible scheduling</li>
                  <li>Cost optimization</li>
                  <li>Comprehensive coverage</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="training-benefits">
            <h2>Benefits of Our Training & Consultation Programs</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>✓ Increased Safety</h3>
                <p>Reduce accidents and ensure compliance with safety standards</p>
              </div>
              <div className="benefit-item">
                <h3>✓ Improved Efficiency</h3>
                <p>Maximize equipment utilization and productivity</p>
              </div>
              <div className="benefit-item">
                <h3>✓ Reduced Downtime</h3>
                <p>Minimize equipment issues through proper operation and maintenance</p>
              </div>
              <div className="benefit-item">
                <h3>✓ Cost Savings</h3>
                <p>Extend equipment lifespan and reduce repair costs</p>
              </div>
              <div className="benefit-item">
                <h3>✓ Compliance Assurance</h3>
                <p>Ensure your team meets all regulatory requirements</p>
              </div>
              <div className="benefit-item">
                <h3>✓ Confidence Building</h3>
                <p>Empower your team with knowledge and skills</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="training-cta-section">
        <div className="container">
          <div className="training-cta-content">
            <h2>Ready to Get Started?</h2>
            <p>
              Schedule a consultation to discuss your training and consultation needs. We'll work with you to develop 
              a customized program that fits your requirements.
            </p>
            <div className="cta-buttons">
              <HeroCTA text="Schedule Consultation" />
              <Link to="/services" className="secondary-cta">
                View All Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default TrainingAndConsultationPage
